import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, first } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Espelha NoticiaFeedDTO (backend snr-leilao-frete-filter, GET /noticia/feed).
 * `conteudo` já vem completo (markdown ou HTML) — o item chega "fechado" pra exibição,
 * mas a chamada já traz tudo, sem round-trip extra ao expandir.
 */
export interface NewsFeedItem {
  id: number;
  titulo: string;
  subtitulo: string;
  conteudo: string;
  publicoEmbarcador: boolean;
  publicoTransportador: boolean;
  dataCriacao: string;
  dataAtualizacao: string | null;
  autorEmail: string | null;
  lida: boolean;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface NewsFeedParams {
  page: number;
  size: number;
  fromDate?: string;
  toDate?: string;
  /** Busca parcial (like/contains) pelo título — filtragem é feita no backend. */
  title?: string;
  /** undefined = todos, true = somente lidas, false = somente não lidas. */
  readStatus?: boolean;
}

@Injectable({ providedIn: 'root' })
export class NewsService {
  private readonly http = inject(HttpClient);

  getFeed(params: NewsFeedParams): Observable<PaginatedResponse<NewsFeedItem>> {
    let httpParams = new HttpParams()
      .set('page', params.page)
      .set('size', params.size)
      .set('sort', 'dataCriacao,desc');

    // Nomes de query param fixados pelo contrato do backend (FiltroNoticiaDTO,
    // snr-leilao-frete-filter) — por isso continuam em português aqui, mesmo com os
    // nomes dos campos do lado do front em inglês.
    if (params.fromDate) {
      httpParams = httpParams.set('deDataCriacao', params.fromDate);
    }
    if (params.toDate) {
      httpParams = httpParams.set('ateDataCriacao', params.toDate);
    }
    if (params.title) {
      httpParams = httpParams.set('titulo', params.title);
    }
    if (params.readStatus !== undefined) {
      httpParams = httpParams.set('lida', params.readStatus);
    }

    return this.http
      .get<PaginatedResponse<NewsFeedItem>>(`${environment.API_FILTER}/noticia/feed`, {
        params: httpParams,
      })
      .pipe(first());
  }

  getUnreadCount(): Observable<{ total: number }> {
    return this.http
      .get<{ total: number }>(`${environment.API_FILTER}/noticia/nao-lidas/count`)
      .pipe(first());
  }

  /** Escrita: fica no backend principal (snr-leilao-frete-backend), não no filter. */
  markAsRead(id: number): Observable<void> {
    return this.http
      .post<void>(`${environment.API_BACK}/noticia/${id}/marcar-lida`, {})
      .pipe(first());
  }
}
